import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["tab", "panel", "mobileLabel"]
  static values = { initialIndex: Number }
  static TAB_QUERY_PARAM = "tab"

  connect() {
    const maxIndex = Math.max(this.tabTargets.length - 1, 0)
    const resolvedIndex = this.resolveInitialIndex(maxIndex)
    this.index = Math.min(Math.max(resolvedIndex, 0), maxIndex)
    if (this.tabTargets.length) this.activate(this.index)
  }

  switch(event) {
    event.preventDefault() // Prevent default if it's a link or button
    const index = parseInt(event.target.closest("[data-index]").dataset.index, 10)
    this.activate(index)

    // Close dropdown on mobile if present (DaisyUI relies on focus, so blurring helps)
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
  }

  activate(index) {
    this.index = index

    // Update Tabs (Desktop & Mobile Dropdown Items)
    this.tabTargets.forEach((el) => {
      const tabIndex = parseInt(el.dataset.index, 10)
      const isSelected = tabIndex === index

      el.setAttribute("aria-selected", isSelected)
      el.setAttribute("tabindex", isSelected ? "0" : "-1")

      // Toggle active classes if needed, or rely on CSS attribute selectors (aria-selected)
      if (isSelected) {
        el.classList.add("active", "text-white", "bg-white/10")
        el.classList.remove("text-gray-400")
      } else {
        el.classList.remove("active", "text-white", "bg-white/10")
        el.classList.add("text-gray-400")
      }
    })

    // Update Panels
    this.panelTargets.forEach((el, i) => {
      el.setAttribute("aria-hidden", String(i !== index))
      el.classList.toggle("hidden", i !== index)
    })

    // Update Mobile Dropdown Label
    if (this.hasMobileLabelTarget) {
      const activeTab = this.tabTargets.find(t => parseInt(t.dataset.index, 10) === index && !t.closest('.dropdown-content'))
      // Or just find the first one that matches index
      const selectedText = activeTab ? activeTab.innerText : "Menú"
      this.mobileLabelTarget.innerText = selectedText
    }

    this.persistState(index)
  }

  resolveInitialIndex(maxIndex) {
    const params = new URLSearchParams(window.location.search)
    const fromQuery = Number.parseInt(params.get(this.constructor.TAB_QUERY_PARAM), 10)
    if (Number.isInteger(fromQuery)) return fromQuery

    const fromStorage = Number.parseInt(sessionStorage.getItem(this.storageKey), 10)
    if (Number.isInteger(fromStorage)) return fromStorage

    const hasInitialIndex = this.hasInitialIndexValue && Number.isInteger(this.initialIndexValue)
    return hasInitialIndex ? this.initialIndexValue : 0
  }

  persistState(index) {
    sessionStorage.setItem(this.storageKey, String(index))
    const url = new URL(window.location.href)
    url.searchParams.set(this.constructor.TAB_QUERY_PARAM, String(index))
    window.history.replaceState({}, "", url)
  }

  get storageKey() {
    return `tabs:${window.location.pathname}`
  }
}
